import { useNode, useEditor } from "@craftjs/core";
import React, { useContext, useState } from "react";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { GrigoraEditorContext } from "../../editor/context";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  RadioButtonInput,
  IconInput,
  NumberInput,
  EditPopover,
  TextInput,
  ToggleInput,
  BoxShadowInput,
  MarginInput,
  RangeNumberInput,
  TabsV2,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isEmpty } from "lodash";
import Toast from "../../toast/toast";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { changeTransition, isUpdatedFromDefault } from "../../utils";

const ALIGNVERTICALITEMS = [
  {
    label: "Top",
    value: "start",
  },
  {
    label: "Center",
    value: "center",
  },
  {
    label: "Bottom",
    value: "end",
  },
];

export const PortalCloseSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
  } = useEditor();

  const {
    align,
    alignTablet,
    alignMobile,
    alignVertical,
    alignVerticalTablet,
    alignVerticalMobile,
    width,
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    background,
    color,
    boxShadow,
    border,
    borderRadius,
    margin,
    marginTablet,
    marginMobile,
    padding,
    paddingTablet,
    paddingMobile,
    hoverBackground,
    hoverColor,
    hoverBoxShadow,
    hoverBorder,
    hoverBorderRadius,
    hoverMargin,
    hoverMarginTablet,
    hoverMarginMobile,
    hoverPadding,
    hoverPaddingTablet,
    hoverPaddingMobile,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <div className="text-white text-xs">
            Clicking this block in frontend will close the parent opened portal.
          </div>
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: width },
            { key: widthTablet },
            { key: widthMobile },
            { key: height },
            { key: heightTablet },
            { key: heightMobile },
            { key: align },
            { key: alignTablet },
            { key: alignMobile },
            { key: alignVertical },
            { key: alignVerticalTablet },
            { key: alignVerticalMobile },
          ])}
          id={`block-setting-${id}-dimensions`}
          title={"Dimensions"}
          initialOpen={true}
        >
          <UnitInput
            resetValue={undefined}
            isResponsive={true}
            value={width}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
          />

          <UnitInput
            resetValue={undefined}
            isResponsive={true}
            value={height}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
          <AlignInput
            isResponsive={true}
            value={align}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <SelectInput
            isResponsive={true}
            isResetVisible={false}
            value={alignVertical}
            valueTablet={alignVerticalTablet}
            valueMobile={alignVerticalMobile}
            onChange={(val) => {
              setProp((props) => {
                props.alignVertical = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignVerticalTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignVerticalMobile = val;
              });
            }}
            options={ALIGNVERTICALITEMS}
            label="Verticl Align"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          colorPropKey: "color",
          colorProp: color,
          backgroundPropKey: "background",
          backgroundProp: background,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          colorPropKey: "hoverColor",
          colorProp: hoverColor,
          backgroundPropKey: "hoverBackground",
          backgroundProp: hoverBackground,
        }),
      },
    ];

    function handleColorStates({
      colorPropKey,
      colorProp,
      backgroundPropKey,
      backgroundProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={colorProp}
            onChange={(val) => {
              setProp((props) => {
                props[colorPropKey] = val;
              });
            }}
            label={"Color"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundProp}
            onChange={(val) => {
              setProp((props) => {
                props[backgroundPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(backgroundPropKey === "hoverColor" ||
            colorPropKey === "hoverBackground") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing={"1rem"}>
            <TabsV2 items={colorTabs} />
          </Spacer>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    const borderRadiusMarginPaddingBoxShadowTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBorderRadiusMarginPaddingBoxShadowStates({
          borderPropKey: "border",
          borderProp: border,
          borderRadiusPropKey: "borderRadius",
          borderRadiusProp: borderRadius,
          marginPropKey: "margin",
          marginProp: margin,
          marginTabletPropKey: "marginTablet",
          marginTabletProp: marginTablet,
          marginMobilePropKey: "marginMobile",
          marginMobileProp: marginMobile,
          paddingPropKey: "padding",
          paddingProp: padding,
          paddingTabletPropKey: "paddingTablet",
          paddingTabletProp: paddingTablet,
          paddingMobilePropKey: "paddingMobile",
          paddingMobileProp: paddingMobile,
          boxShadowPropKey: "boxShadow",
          boxShadowProp: boxShadow,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderRadiusMarginPaddingBoxShadowStates({
          borderPropKey: "hoverBorder",
          borderProp: hoverBorder,
          borderRadiusPropKey: "hoverBorderRadius",
          borderRadiusProp: hoverBorderRadius,
          marginPropKey: "hoverMargin",
          marginProp: hoverMargin,
          marginTabletPropKey: "hoverMarginTablet",
          marginTabletProp: hoverMarginTablet,
          marginMobilePropKey: "hoverMarginMobile",
          marginMobileProp: hoverMarginMobile,
          paddingPropKey: "hoverPadding",
          paddingProp: hoverPadding,
          paddingTabletPropKey: "hoverPaddingTablet",
          paddingTabletProp: hoverPaddingTablet,
          paddingMobilePropKey: "hoverPaddingMobile",
          paddingMobileProp: hoverPaddingMobile,
          boxShadowPropKey: "hoverBoxShadow",
          boxShadowProp: hoverBoxShadow,
        }),
      },
    ];

    function handleBorderRadiusMarginPaddingBoxShadowStates({
      borderPropKey,
      borderProp,
      borderRadiusPropKey,
      borderRadiusProp,
      marginPropKey,
      marginProp,
      marginTabletPropKey,
      marginTabletProp,
      marginMobilePropKey,
      marginMobileProp,
      paddingPropKey,
      paddingProp,
      paddingTabletPropKey,
      paddingTabletProp,
      paddingMobilePropKey,
      paddingMobileProp,
      boxShadowPropKey,
      boxShadowProp,
    }) {
      return (
        <>
          <MarginInput
            resetValue={undefined}
            isResponsive={true}
            value={marginProp}
            valueTablet={marginTabletProp}
            valueMobile={marginMobileProp}
            onChange={(val) => {
              setProp((props) => {
                props[marginPropKey] = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props[marginTabletPropKey] = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props[marginMobilePropKey] = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            label={"Padding"}
            isResponsive={true}
            valueTablet={paddingTabletProp}
            valueMobile={paddingMobileProp}
            value={paddingProp}
            onChange={(val) => {
              setProp((props) => {
                props[paddingPropKey] = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props[paddingTabletPropKey] = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props[paddingMobilePropKey] = val;
              });
            }}
          />
          <BorderInput
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={borderRadiusProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderRadiusPropKey] = val;
              });
            }}
            label={"Border Radius"}
          />

          <BoxShadowInput
            resetValue={undefined}
            value={boxShadowProp}
            onChange={(val) => {
              setProp((props) => {
                props[boxShadowPropKey] = val;
              });
            }}
          />
          {(paddingPropKey === "hoverPadding" ||
            paddingTabletPropKey === "hoverPaddingTablet" ||
            paddingMobilePropKey === "hoverPaddingMobile" ||
            boxShadowPropKey === "hoverBoxShadow" ||
            marginMobilePropKey === "hoverMarginMobile" ||
            marginTabletPropKey === "hoverMarginTablet" ||
            marginPropKey === "hoverMargin" ||
            borderRadiusPropKey === "hoverBorderRadius" ||
            borderPropKey === "hoverBorder") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[
                  ("border", "border-radius", "margin", "padding", "box-shadow")
                ]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  [
                    "border",
                    "border-radius",
                    "margin",
                    "padding",
                    "box-shadow",
                  ],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing="1rem">
            <TabsV2 items={borderRadiusMarginPaddingBoxShadowTabs} />
          </Spacer>

          {supports &&
            supports.map((support) => {
              var Component = supportLibrary[support]?.component;

              if (!Component) {
                return;
              }

              return (
                <>
                  <Component
                    block={{
                      id,
                      props,
                      dom,
                    }}
                    actions={{
                      setProp,
                    }}
                  ></Component>
                </>
              );
            })}
        </div>
      </>
    );
  }

  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: width },
        { key: widthTablet },
        { key: widthMobile },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
        { key: align },
        { key: alignTablet },
        { key: alignMobile },
        { key: alignVertical },
        { key: alignVerticalTablet },
        { key: alignVerticalMobile },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: color },
        { key: background },
        { key: hoverColor },
        { key: hoverBackground },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: margin },
        { key: marginTablet },
        { key: marginMobile },
        { key: paddingTablet },
        { key: paddingMobile },
        { key: padding },
        { key: border },
        { key: borderRadius },
        { key: boxShadow },
        { key: hoverMargin },
        { key: hoverMarginTablet },
        { key: hoverMarginMobile },
        { key: hoverPaddingTablet },
        { key: hoverPaddingMobile },
        { key: hoverPadding },
        { key: hoverBorder },
        { key: hoverBorderRadius },
        { key: hoverBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
